import { useInView } from "@/hooks/useAnimations";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

const regions = [
  {
    city: "Düsseldorf",
    text: "Online-Pflegeberatung für Düsseldorf und Umgebung. Keine Anfahrt in die Landeshauptstadt nötig — Ihre Beratung nach §37.3 SGB XI bequem per Video.",
  },
  {
    city: "Köln",
    text: "Pflegeberatung für Köln und den Rhein-Erft-Kreis. Digital, persönlich und kostenfrei über Ihre Pflegekasse.",
  },
  {
    city: "Essen",
    text: "Pflegeberatung im Ruhrgebiet — von Essen bis Gelsenkirchen. Schnelle Termine ohne Wartezeit.",
  },
  {
    city: "Krefeld",
    text: "Direkt aus der Nachbarschaft: Pflegeberatung für Krefeld, Viersen und den Niederrhein per Video-Call.",
  },
  {
    city: "Duisburg",
    text: "Online-Pflegeberatung für Duisburg und den westlichen Niederrhein. Zertifiziert und kostenfrei.",
  },
  {
    city: "Mönchengladbach",
    text: "Pflegeberatung für Mönchengladbach und Umgebung. Ihr Beratungseinsatz nach §37 SGB XI — einfach digital.",
  },
  {
    city: "Dortmund",
    text: "Online-Pflegeberatung für Dortmund und das östliche Ruhrgebiet. Flexibel, schnell und von der Pflegekasse bezahlt.",
  },
  {
    city: "Bonn",
    text: "Pflegeberatung für Bonn und den Rhein-Sieg-Kreis. Per Video-Call beraten — ohne lange Anfahrtswege.",
  },
  {
    city: "Münster",
    text: "Online-Pflegeberatung für Münster und das Münsterland. Persönliche Beratung, digital und unkompliziert.",
  },
  {
    city: "Grefrath",
    text: "Unser Hauptsitz: Pflegeberatung direkt aus Grefrath am Niederrhein. Lokal verwurzelt, digital verfügbar.",
    highlight: true,
  },
];

export default function RegionenNRW() {
  const [ref, isInView] = useInView();

  const scrollToBooking = () => {
    const el = document.querySelector("#termin-buchen");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      id="regionen"
      data-testid="regionen-section"
      className="py-20 lg:py-28 bg-[#F8FAFC]"
      aria-label="Online Pflegeberatung in NRW – Regionen und Städte"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-14 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 bg-cyan-50 px-4 py-1.5 rounded-full mb-4">
            <MapPin className="h-3.5 w-3.5" />
            Ganz NRW
          </span>
          <h2 className="font-['Manrope'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            Online Pflegeberatung in Ihrer Region
          </h2>
          <p className="text-base sm:text-lg text-slate-500 leading-relaxed">
            Egal ob Düsseldorf, Köln oder Münster — als digitaler Pflegeberatungsdienst sind wir in
            ganz Nordrhein-Westfalen für Sie da. Ohne Anfahrt, ohne Wartezeit.
          </p>
        </div>

        {/* City grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {regions.map((r, i) => (
            <article
              key={r.city}
              data-testid={`region-${r.city.toLowerCase()}`}
              className={`rounded-2xl p-5 transition-all duration-500 ${
                r.highlight
                  ? 'bg-cyan-700 text-white shadow-lg'
                  : 'bg-white border border-slate-200/60 hover:shadow-md hover:-translate-y-0.5'
              } ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: isInView ? `${i * 60}ms` : '0ms' }}
            >
              <div className="flex items-center gap-2 mb-2">
                <MapPin className={`h-4 w-4 ${r.highlight ? 'text-cyan-200' : 'text-cyan-600'}`} />
                <h3 className={`font-['Manrope'] text-lg font-bold ${r.highlight ? 'text-white' : 'text-slate-800'}`}>
                  {r.city}
                </h3>
              </div>
              <p className={`text-sm leading-relaxed ${r.highlight ? 'text-cyan-100' : 'text-slate-500'}`}>
                {r.text}
              </p>
            </article>
          ))}
        </div>

        {/* SEO-rich bottom text + CTA */}
        <div className={`mt-12 text-center transition-all duration-700 delay-300 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p className="text-sm text-slate-400 max-w-3xl mx-auto mb-6 leading-relaxed">
            Unsere Online-Pflegeberatung ist verfügbar in allen Städten und Kreisen in NRW, darunter
            Aachen, Bochum, Wuppertal, Bielefeld, Oberhausen, Hagen, Hamm, Solingen, Leverkusen,
            Neuss, Paderborn, Siegen, Recklinghausen, Moers, Bergisch Gladbach, Gütersloh, Remscheid, Viersen und viele weitere.
            Jede Person mit Pflegegrad 1–5 in Nordrhein-Westfalen hat Anspruch auf kostenlose Pflegeberatung nach §37 Abs. 3 SGB XI.
          </p>
          <Button
            data-testid="regionen-cta-btn"
            onClick={scrollToBooking}
            className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-8 py-3 text-base font-semibold shadow-sm hover:shadow-md transition-all"
          >
            Jetzt Termin buchen — egal wo in NRW
          </Button>
        </div>
      </div>
    </section>
  );
}
